"use client";

import { FC, useEffect, useRef, useState } from "react";
import {
  chartOptions,
  durationIntervalValue,
  graphDurationBtns,
  sharesColors,
} from "./StockPerformanceGraphConstants";
import {
  convertChartData,
  getDateRange,
  handlePerformanceGraphDL,
} from "./StockPerformanceGraphUtils";
import { getStockChartData } from "../../services/TradingMallService";
import { selectLoginStatus, useAppSelector } from "@repo/web-store";
import { createChart } from "lightweight-charts";
import { IPChgAnalysisData } from "../../interfaces/StockTechnicalSummary.interface";

interface StockPerformanceGraphProps {
  formattedSymbol: string;
  compName?: string;
  exchId: string;
  chgPerValue?: number;
  pChgAnalysisValue?: IPChgAnalysisData;
}
const StockPerformanceGraph: FC<StockPerformanceGraphProps> = ({
  formattedSymbol,
  compName,
  exchId,
  chgPerValue,
  pChgAnalysisValue
}) => {
  const loginStatus = useAppSelector(selectLoginStatus);
  const chartDivRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<any>(null);
  const [duration, setDuration] = useState("1D");
  const [chartReady, setChartReady] = useState(false);
  const [tooltipData, setTooltipData] = useState<{
    price: number;
    time: string;
    x: number;
  } | null>(null);

  useEffect(() => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.remove();
    }
    const intervalValue = getDateRange(duration);
    async function fetchChartData() {
      const data = await getStockChartData({
        exchId: Number(exchId),
        token_symbol: formattedSymbol,
        fromDate:
          intervalValue?.fromDate || new Date().toISOString().slice(0, 10),
        toDate: intervalValue?.toDate || new Date().toISOString().slice(0, 10),
        interval:
          durationIntervalValue[duration as keyof typeof durationIntervalValue],
      });
      const cdata = convertChartData(data?.chartData);
      const chart = createChart(chartDivRef.current!, {
        ...chartOptions,
        crosshair: {
          mode: 0,
          vertLine: {
            visible: true,
            labelVisible: false,
            width: 1,
            color: "#999",
          },
          horzLine: {
            visible: false,
            labelVisible: false,
          },
        },
        timeScale: {
          ...chartOptions.timeScale,
          visible: false,
          borderVisible: false,
        },
      });

      chartInstanceRef.current = chart;

      const isRising =
        (cdata?.[cdata.length - 1]?.value ?? 0) > (cdata?.[0]?.value ?? 0);

      const areaSeries = chart.addAreaSeries({
        priceLineVisible: false,
        lastValueVisible: false,
      });

      areaSeries.applyOptions({
        lineColor: isRising ? sharesColors.greenDeep : sharesColors.red,
        lineWidth: 1,
        lastPriceAnimation: 1,
        bottomColor: isRising
          ? "rgba(52, 209, 120, 0.1)"
          : "rgba(230, 38, 30, 0.2)",
        topColor: isRising ? sharesColors.greenDeep : sharesColors.red,
      });

      areaSeries.setData(cdata);
      chart.timeScale().fitContent();
      setChartReady(true);
      chart.timeScale().fitContent();

      chart.subscribeCrosshairMove((param: any) => {
        if (
          param?.time &&
          param.seriesData.size > 0 &&
          param.point &&
          areaSeries
        ) {
          const seriesData = param.seriesData.get(areaSeries);
          const price =
            seriesData && "value" in seriesData ? seriesData.value : 0;

          let timeStr = "";
          if (typeof param.time === "number") {
            const date = new Date(param.time * 1000);
            const hours24 = date.getUTCHours().toString().padStart(2, "0");
            const minutes = date.getUTCMinutes().toString().padStart(2, "0");
            const ampm = +hours24 >= 12 ? "PM" : "AM";

            timeStr = `${hours24}:${minutes} ${ampm}`;
          } else if (typeof param.time === "object") {
            const { year, month, day } = param.time;
            timeStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          }

          setTooltipData({
            price,
            time: timeStr,
            x: param.point.x,
          });
        } else {
          setTooltipData(null);
        }
      });
    }

    fetchChartData();
  }, [duration]);

  useEffect(() => {
    const handleResize = () => {
      chartInstanceRef.current?.resize(
        chartDivRef.current!.clientWidth,
        chartDivRef.current!.clientHeight,
      );
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      chartInstanceRef.current?.remove();
    };
  }, []);

  return (
    <>
      <div className="relative mt-6 h-[225px] md:mb-0 md:mt-3 md:h-[225px]">
        {tooltipData && (
          <div
            className="text-bfl-blue absolute z-10 text-sm"
            style={{
              top: "-8px",
              left: tooltipData.x < 50 ? "0px" : `${tooltipData.x}px`,
              transform:
                tooltipData.x < 50
                  ? "none"
                  : tooltipData.x > (chartDivRef.current?.clientWidth ?? 0) - 50
                    ? "translateX(-100%)"
                    : "translateX(-50%)",
              pointerEvents: "none",
              whiteSpace: "nowrap",
              backgroundColor: "transparent",
              fontSize: "12px",
            }}
          >
            ₹{tooltipData.price.toFixed(2)} | {tooltipData.time}
          </div>
        )}

        <div
          ref={chartDivRef}
          className="min-h-[225px] md:aspect-[3/1] md:min-h-0"
        />
        <div style={{ display: "none"}} className="display-none">
          {`YTD 1 Week 1 Months 3 Months 6 Months 1 Year ${chgPerValue}% ${pChgAnalysisValue?.oneWeek}% ${pChgAnalysisValue?.oneMth}% ${pChgAnalysisValue?.threeMth}% ${pChgAnalysisValue?.sixMth}% ${pChgAnalysisValue?.oneYr}%`}
        </div>
      </div>

      <div
        className={`mt-4 flex gap-4 text-gray-500 transition-opacity duration-300 md:mt-[28px] ${chartReady ? "opacity-100" : "pointer-events-none opacity-0"}`}
      >
        {graphDurationBtns?.map((btnValue, index) => (
          <button
            key={`duration_${index}`}
            type="button"
            onClick={() => {
              // Track button click
              handlePerformanceGraphDL(
                loginStatus,
                compName || "Unknown Company",
                btnValue,
                String(index),
              );
              setDuration(btnValue);
            }}
            className={`duration-btn cursor-pointer text-sm ${
              btnValue === duration ? "text-bfl-blue bg-blue-100" : ""
            } rounded-[25px] px-2 py-1`}
            value={btnValue}
          >
            {btnValue}
          </button>
        ))}
      </div>
    </>
  );
};

export default StockPerformanceGraph;
